import { ArrowLeftIcon } from '@/components/Inbox/icons'
import { cn } from '@/lib/utils'
import { Link } from '@/i18n/navigation'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { NAV_DATA } from './data'
import { ChevronUp } from './icons'
import { MenuItem } from './menu-item'
import { useSidebarContext } from './sidebar-context'
import useAccess from '@/hooks/use-access'
import Access from '@/hooks/Access'

// Pastel palette shared with the dashboard cards
const ITEM_COLORS: Record<string, { idle: string; active: string; panel: string; dot: string }> = {
  cyan: {
    idle: 'text-[#D6F3F8] hover:bg-[#D6F3F8] hover:text-slate-900',
    active: 'bg-[#D6F3F8] hover:bg-[#D6F3F8] text-slate-900 hover:text-slate-900 ring-[#D6F3F8]/40',
    panel: 'bg-[#D6F3F8] border-cyan-200',
    dot: 'bg-[#D6F3F8]',
  },
  green: {
    idle: 'text-[#DCF8CE] hover:bg-[#DCF8CE] hover:text-slate-900',
    active: 'bg-[#DCF8CE] hover:bg-[#DCF8CE] text-slate-900 hover:text-slate-900 ring-[#DCF8CE]/40',
    panel: 'bg-[#DCF8CE] border-green-200',
    dot: 'bg-[#DCF8CE]',
  },
  purple: {
    idle: 'text-[#F3E2FF] hover:bg-[#F3E2FF] hover:text-slate-900',
    active: 'bg-[#F3E2FF] hover:bg-[#F3E2FF] text-slate-900 hover:text-slate-900 ring-[#F3E2FF]/40',
    panel: 'bg-[#F3E2FF] border-purple-200',
    dot: 'bg-[#F3E2FF]',
  },
  amber: {
    idle: 'text-[#FEF3D6] hover:bg-[#FEF3D6] hover:text-slate-900',
    active: 'bg-[#FEF3D6] hover:bg-[#FEF3D6] text-slate-900 hover:text-slate-900 ring-[#FEF3D6]/40',
    panel: 'bg-[#FEF3D6] border-amber-200',
    dot: 'bg-[#FEF3D6]',
  },
  pink: {
    idle: 'text-[#FFE0EC] hover:bg-[#FFE0EC] hover:text-slate-900',
    active: 'bg-[#FFE0EC] hover:bg-[#FFE0EC] text-slate-900 hover:text-slate-900 ring-[#FFE0EC]/40',
    panel: 'bg-[#FFE0EC] border-pink-200',
    dot: 'bg-[#FFE0EC]',
  },
}

const TILE_CLASSES =
  'flex h-10 w-10 items-center justify-center rounded-2xl bg-white/5 p-0 shadow-sm transition-all duration-200 hover:scale-105'
const TILE_ACTIVE_CLASSES = ''

// Marks items flagged with isPro in NAV_DATA
function SoonBadge() {
  return (
    <span className="pointer-events-none absolute -bottom-1.5 left-1/2 -translate-x-1/2 rounded-full bg-primary px-1 py-0.5 text-[8px] leading-none font-bold tracking-wide text-white uppercase ring-2 ring-dark">
      Soon
    </span>
  )
}

type OpenFlyout ={ title: string; top: number; left: number }

export function Sidebar() {
  const pathname = usePathname()
  const access: any = useAccess()
  const { setIsOpen, isOpen, isMobile, toggleSidebar } = useSidebarContext()
  const [flyout, setFlyout] = useState<OpenFlyout | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  const canAccess = (acc?: readonly string[]) => !acc || acc.some(a => access[a])

  const toggleFlyout = (title: string) => {
    if (flyout?.title === title) {
      setFlyout(null)
      return
    }
    const rect = triggerRefs.current[title]?.getBoundingClientRect()
    if (!rect) return
    setFlyout({ title, top: rect.top, left: rect.right + 16 })
  }

  // Close the flyout on navigation
  useEffect(() => {
    setFlyout(null)
  }, [pathname])

  // Close the flyout on outside click, Escape, resize, or when the sidebar collapses
  useEffect(() => {
    if (!flyout) return

    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (panelRef.current?.contains(target)) return
      if (triggerRefs.current[flyout.title]?.contains(target)) return
      setFlyout(null)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      triggerRefs.current[flyout.title]?.focus()
      setFlyout(null)
    }
    const close = () => setFlyout(null)

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', close)
    }
  }, [flyout])

  useEffect(() => {
    if (!isOpen) setFlyout(null)
  }, [isOpen])

  const flyoutItem: any = flyout
    ? NAV_DATA.flatMap(section => section.items as readonly any[]).find(item => item.title === flyout.title)
    : null
  const flyoutColor = ITEM_COLORS[flyoutItem?.color] ?? ITEM_COLORS.cyan

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 transition-opacity duration-300"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'max-w-[80px] overflow-hidden bg-dark transition-[width] duration-200 ease-linear',
          isMobile ? 'fixed top-0 bottom-0 z-50' : 'sticky top-0 h-screen',
          isOpen ? 'w-full pb-20' : 'w-0',
        )}
        aria-label="Main navigation"
        aria-hidden={!isOpen}
        inert={!isOpen}>
        <div className="flex h-full flex-col py-4 px-3 mt-6">
          <div className="relative pr-4.5">
            {isMobile && (
              <button
                onClick={toggleSidebar}
                className="absolute top-1/2 right-4.5 left-3/4 -translate-y-1/2 text-right">
                <span className="sr-only">Close Menu</span>
                <ArrowLeftIcon className="ml-auto size-7" />
              </button>
            )}
          </div>

          {/* Navigation */}
          <div
            className="custom-scrollbar mt-6 flex-1 overflow-y-auto pr-3 min-[850px]:mt-10"
            onScroll={() => flyout && setFlyout(null)}>
            {NAV_DATA.map((section, sectionIndex) => (
              <Access key={section.label} accessible={section.access.some(acc => access[acc])}>
                <div className="mb-5">
                  {sectionIndex > 0 && <div className="mx-auto mb-5 h-px w-8 rounded-full bg-white/15" aria-hidden="true" />}
                  <nav role="navigation" aria-label={section.label || 'Main'}>
                    <ul className="flex flex-col items-center gap-3">
                      {section.items.map((item: any) => {
                        const color = ITEM_COLORS[item.color] ?? ITEM_COLORS.cyan
                        const subItems: any[] = item.items ?? []

                        // Parent item: opens a flyout with its nested links
                        if (subItems.length > 0) {
                          const isActive = subItems.some(sub => pathname.includes(sub.url))
                          const isOpenFlyout = flyout?.title === item.title
                          return (
                            <Access key={item.title} accessible={canAccess(item.access)}>
                              <li>
                                <button
                                  ref={el => {
                                    triggerRefs.current[item.title] = el
                                  }}
                                  type="button"
                                  onClick={() => toggleFlyout(item.title)}
                                  title={item.title}
                                  aria-label={item.title}
                                  aria-expanded={isOpenFlyout}
                                  aria-controls="sidebar-flyout"
                                  className={cn(
                                    TILE_CLASSES,
                                    'relative font-medium',
                                    isActive || isOpenFlyout ? cn(color.active, TILE_ACTIVE_CLASSES) : color.idle,
                                  )}>
                                  {item.icon && <item.icon className="size-6 shrink-0" aria-hidden="true" />}
                                  {/* Marks items that have nested links */}
                                  <span
                                    className={cn(
                                      'absolute -top-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-dark',
                                      color.dot,
                                    )}
                                    aria-hidden="true"
                                  />
                                  {item.isPro && <SoonBadge />}
                                </button>
                              </li>
                            </Access>
                          )
                        }

                        const href = 'url' in item ? item.url + '' : '/' + item.title.toLowerCase().split(' ').join('-')
                        const isActive = pathname.includes(href)
                        return (
                          <li key={item.title}>
                            <MenuItem
                              className={cn(TILE_CLASSES, isActive ? cn(color.active, TILE_ACTIVE_CLASSES) : color.idle)}
                              as="link"
                              access={item?.access}
                              href={href}
                              label={item.title}
                              isActive={isActive}
                            >
                              {item.icon && <item.icon className="size-6 shrink-0" aria-hidden="true" />}
                              {item.isPro && <SoonBadge />}
                            </MenuItem>
                          </li>
                        )
                      })}
                    </ul>
                  </nav>
                </div>
              </Access>
            ))}
          </div>
        </div>
      </aside>

      {/* Flyout for nested items — rendered outside the aside so it isn't clipped */}
      {flyout && flyoutItem && (
        <div
          id="sidebar-flyout"
          ref={panelRef}
          style={{ top: flyout.top, left: flyout.left }}
          className={cn(
            'fixed z-[60] w-60 rounded-3xl border p-2.5 text-slate-800 shadow-xl',
            flyoutColor.panel,
          )}>
          {/* Pointer towards the trigger */}
          <span
            className={cn('absolute top-4 -left-1.5 size-3 rotate-45 rounded-sm', flyoutColor.dot)}
            aria-hidden="true"
          />
          <div className="relative flex items-center gap-2 px-2.5 pt-1 pb-2.5">
            {flyoutItem.icon && (
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/80 shadow-sm">
                <flyoutItem.icon className="size-4 text-slate-900" aria-hidden="true" />
              </span>
            )}
            <span className="text-sm font-bold text-slate-900">{flyoutItem.title}</span>
          </div>
          <nav aria-label={flyoutItem.title}>
            <ul className="relative flex flex-col gap-1">
              {(flyoutItem.items as any[]).map(sub => {
                const isActive = pathname.includes(sub.url)
                return (
                  <Access key={sub.url} accessible={canAccess(sub.access)}>
                    <li>
                      <Link
                        href={sub.url}
                        aria-current={isActive ? 'page' : undefined}
                        onClick={() => {
                          setFlyout(null)
                          if (isMobile) toggleSidebar()
                        }}
                        className={cn(
                          'flex items-center justify-between rounded-2xl px-3 py-2 text-xs font-semibold transition',
                          isActive
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-700 hover:bg-white/60 hover:text-slate-900',
                        )}>
                        {sub.title}
                        <ChevronUp
                          className={cn('size-3.5 rotate-90 transition', isActive ? 'opacity-100' : 'opacity-40')}
                          aria-hidden="true"
                        />
                      </Link>
                    </li>
                  </Access>
                )
              })}
            </ul>
          </nav>
        </div>
      )}
    </>
  )
}
